import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Trash2, Pencil, Users, X, Calendar, Camera, Image as ImageIcon, MapPin, UserPlus, Palette, Star } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ImageUploadOrUrl from '../../components/ImageUploadOrUrl';
import MapPinPicker from '../../components/MapPinPicker';
import RichEditor from '../../components/RichEditor';

const EMPTY_SPEAKER = { name: '', role: '', bio: '', photo: null, linkedin: '' };
const EMPTY_SPONSOR = { name: '', logo: null, url: '', tier: 'Auspiciador' };

const EMPTY = {
  title: '', subtitle: '', description: '', cover_image: null, gallery: [],
  starts_at: '', ends_at: '', venue: '', address: '', city: 'Santa Cruz', country: 'Bolivia',
  lat: null, lng: null,
  organizer: 'CNIB Bolivia', organizer_logo: null,
  status: 'published',
  tiers: [{ name: 'General', price_usd: 0, member_price_usd: null, is_free: true, quantity: 100 }],
  agenda: '',
  speakers: [],
  highlights: [], faq: '', sponsors: [],
  hashtag: '', primary_color: '#F97316', accent_color: '#0F4C81',
  cta_text: 'Reservar mi lugar', website: '', stream_url: '',
};

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tickets, setTickets] = useState(null);
  const [stats, setStats] = useState({});
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const galleryRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/events');
      setEvents(data || []);
      for (const ev of data || []) {
        api.get(`/admin/events/${ev.event_id}/stats`).then(({ data }) => setStats((s) => ({ ...s, [ev.event_id]: data })));
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (ev) => {
    setEditing(ev.event_id);
    setForm({
      ...EMPTY,
      ...ev,
      tiers: (ev.tiers || []).map((t) => ({ ...t })),
      speakers: (ev.speakers || []).map((s) => (typeof s === 'string' ? { ...EMPTY_SPEAKER, name: s } : s)),
      gallery: ev.gallery || [],
      highlights: ev.highlights || [],
      sponsors: (ev.sponsors || []).map((s) =>
        typeof s === 'string' ? { ...EMPTY_SPONSOR, name: s } : { ...EMPTY_SPONSOR, ...s }
      ),
    });
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...form,
        starts_at: form.starts_at,
        ends_at: form.ends_at,
        lat: form.lat != null ? Number(form.lat) : null,
        lng: form.lng != null ? Number(form.lng) : null,
        tiers: form.tiers.map((t) => ({
          name: t.name, price_usd: Number(t.price_usd || 0),
          member_price_usd: t.member_price_usd ? Number(t.member_price_usd) : null,
          is_free: !!t.is_free, quantity: Number(t.quantity || 100),
        })),
      };
      if (editing) await api.patch(`/admin/events/${editing}`, body);
      else await api.post('/admin/events', body);
      toast.success('Evento guardado');
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar evento y todos sus tickets?')) return;
    await api.delete(`/admin/events/${id}`);
    toast.success('Eliminado'); load();
  };

  const loadTickets = async (id) => {
    const { data } = await api.get(`/admin/events/${id}/tickets`);
    setTickets({ eventId: id, list: data });
  };

  const uploadGallery = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingGallery(true);
    try {
      const urls = [];
      for (const file of files) {
        if (!file.type?.startsWith('image/')) continue;
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (data?.url) urls.push(data.url);
      }
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...urls].slice(0, 20) }));
    } finally { setUploadingGallery(false); }
  };

  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';
  const sectionTitle = (n, label) => (
    <h4 className="mt-5 mb-3 flex items-center gap-2 border-t border-slate-100 pt-5 font-heading text-sm font-extrabold text-slate-900">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cnib-blue text-xs text-white">{n}</span>
      {label}
    </h4>
  );

  return (
    <div className="space-y-5" data-testid="admin-events">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Congresos</p>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Eventos CNIB</h1>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-sm font-bold text-white hover:bg-orange-600" data-testid="admin-events-new">
          <Plus className="h-4 w-4" /> Nuevo evento
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-cnib-blue" /></div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">Aún no hay eventos.</div>
      ) : (
        <div className="space-y-3" data-testid="admin-events-list">
          {events.map((ev) => (
            <div key={ev.event_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-event-${ev.event_id}`}>
              <div className="flex items-start gap-3">
                {ev.cover_image && <img src={ev.cover_image} alt="" className="h-20 w-28 shrink-0 rounded-xl object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{String(ev.starts_at || '').slice(0, 10)} · {ev.city}</p>
                  <p className="truncate font-heading text-base font-extrabold text-slate-900">{ev.title}</p>
                  <p className="truncate text-xs text-slate-500">{ev.venue}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                    {(ev.tiers || []).map((t) => (
                      <span key={t.tier_id} className="rounded-full bg-slate-100 px-2 py-0.5 font-bold text-slate-600">
                        {t.name}: {t.sold}/{t.quantity}
                      </span>
                    ))}
                    {(ev.speakers || []).length > 0 && (
                      <span className="rounded-full bg-violet-100 px-2 py-0.5 font-bold text-violet-700">
                        {ev.speakers.length} speakers
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
                    {stats[ev.event_id]?.checked_in ?? 0}/{stats[ev.event_id]?.total_tickets ?? 0} check-ins
                  </span>
                  <div className="flex gap-1">
                    <a href={`/eventos/${ev.slug}`} target="_blank" rel="noopener noreferrer" className="rounded-full bg-orange-50 p-2 text-orange-600 hover:bg-orange-100" aria-label="Ver micrositio público" data-testid={`admin-event-preview-${ev.event_id}`}>
                      <Star className="h-4 w-4" />
                    </a>
                    <button onClick={() => loadTickets(ev.event_id)} className="rounded-full bg-cnib-blue/10 p-2 text-cnib-blue hover:bg-cnib-blue/20" data-testid={`admin-event-tickets-${ev.event_id}`}>
                      <Users className="h-4 w-4" />
                    </button>
                    <button onClick={() => openEdit(ev)} className="rounded-full bg-slate-100 p-2 text-slate-700 hover:bg-slate-200"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(ev.event_id)} className="rounded-full bg-red-50 p-2 text-red-600 hover:bg-red-100"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 text-xs" data-testid={`admin-event-links-${ev.event_id}`}>
                <span className="font-bold uppercase tracking-wider text-slate-400">Links del evento:</span>
                <a href={`/eventos/${ev.slug}`} target="_blank" rel="noopener noreferrer" className="rounded-full bg-slate-100 px-3 py-1 font-bold text-slate-700 hover:bg-slate-200" data-testid={`admin-event-link-${ev.event_id}`}>
                  /eventos/{ev.slug}
                </a>
                <button
                  type="button"
                  onClick={() => {
                    const base = typeof window !== 'undefined' ? window.location.origin : '';
                    const sub = `${base.replace(/\/\/([^.]+)/, `//${ev.slug}-evento`)}`;
                    navigator.clipboard.writeText(sub);
                    toast.success('Subdominio copiado al portapapeles');
                  }}
                  className="rounded-full bg-cnib-blue/10 px-3 py-1 font-bold text-cnib-blue hover:bg-cnib-blue/20"
                  data-testid={`admin-event-subdomain-${ev.event_id}`}
                  title={`${ev.slug}-evento.cnib.bo`}
                >
                  Copiar subdominio: {ev.slug}-evento.cnib.bo
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const url = `${typeof window !== 'undefined' ? window.location.origin : ''}/eventos/${ev.slug}`;
                    const text = `🎟️ ${ev.title} — ${ev.city}\n${url}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
                  }}
                  className="rounded-full bg-emerald-50 px-3 py-1 font-bold text-emerald-700 hover:bg-emerald-100"
                  data-testid={`admin-event-share-${ev.event_id}`}
                >
                  Compartir por WhatsApp
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal edit/new */}
      {modalOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/70 p-0 sm:items-center sm:p-6">
          <div className="w-full max-w-3xl overflow-y-auto rounded-t-3xl bg-white sm:rounded-3xl" style={{ maxHeight: '92vh' }}>
            <div className="sticky top-0 z-10 flex items-start justify-between border-b border-slate-100 bg-white px-6 py-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-orange-500">Micrositio del evento</p>
                <h3 className="font-heading text-xl font-extrabold text-slate-900">{editing ? 'Editar evento' : 'Nuevo evento'}</h3>
              </div>
              <button type="button" onClick={() => { if (window.confirm('¿Cerrar sin guardar? Se perderán todos los cambios no guardados.')) setModalOpen(false); }} className="rounded-full p-1 hover:bg-slate-100" data-testid="admin-event-close"><X className="h-5 w-5" /></button>
            </div>

            <form onSubmit={save} className="p-6">
              {sectionTitle('1', 'Identidad del evento')}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Título *</label>
                  <input required minLength={4} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="admin-event-title" />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Subtítulo</label>
                  <input value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Descripción comercial — soporta fotos, videos de YouTube y embeds de redes</label>
                  <div className="rounded-xl border border-slate-200 bg-white" data-testid="admin-event-description">
                    <RichEditor value={form.description} onChange={(html) => setForm({ ...form, description: html })} />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Hashtag</label>
                  <input placeholder="#CNIB2026" value={form.hashtag || ''} onChange={(e) => setForm({ ...form, hashtag: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Estado</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                    <option value="published">Publicado</option>
                    <option value="draft">Borrador</option>
                    <option value="finished">Finalizado</option>
                  </select>
                </div>
              </div>

              {sectionTitle('2', 'Imagen de portada y galería')}
              <div className="space-y-3">
                <ImageUploadOrUrl value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} sizeHint="1600 × 900 px (hero del micrositio)" testid="admin-event-cover" />
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-[11px] font-bold text-slate-500">Galería del evento ({(form.gallery || []).length}/20)</label>
                    <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadGallery(Array.from(e.target.files || []))} />
                    <button type="button" onClick={() => galleryRef.current?.click()} disabled={uploadingGallery} className="inline-flex items-center gap-1 rounded-full bg-cnib-blue px-3 py-1 text-xs font-bold text-white disabled:opacity-60" data-testid="admin-event-gallery-add">
                      {uploadingGallery ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />}
                      Agregar fotos
                    </button>
                  </div>
                  {(form.gallery || []).length > 0 && (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {form.gallery.map((url, i) => (
                        <div key={i} className="relative aspect-square overflow-hidden rounded-xl">
                          <img src={url} alt="" className="h-full w-full object-cover" />
                          <button type="button" onClick={() => setForm({ ...form, gallery: form.gallery.filter((_, j) => j !== i) })} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-slate-900/80 text-white">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {sectionTitle('3', 'Fecha y ubicación')}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Inicio *</label>
                  <input type="datetime-local" required value={form.starts_at?.slice(0, 16) || ''} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className={inputCls} data-testid="admin-event-start" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Fin *</label>
                  <input type="datetime-local" required value={form.ends_at?.slice(0, 16) || ''} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Sede / Venue *</label>
                  <input required value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Ciudad *</label>
                  <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Dirección exacta</label>
                  <input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-[11px] font-bold text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Ubicación en el mapa</label>
                  <MapPinPicker
                    value={form.lat != null && form.lng != null ? { lat: form.lat, lng: form.lng } : null}
                    onChange={(ll) => setForm({ ...form, lat: ll.lat, lng: ll.lng })}
                    color={form.primary_color || '#F97316'}
                    icon="dot"
                    testid="admin-event-map-picker"
                  />
                </div>
              </div>

              {sectionTitle('4', 'Speakers / Expositores')}
              <div className="space-y-3" data-testid="admin-event-speakers">
                {(form.speakers || []).map((sp, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 grid gap-2 sm:grid-cols-2">
                        <input placeholder="Nombre *" required value={sp.name} onChange={(e) => setForm({ ...form, speakers: form.speakers.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} className={inputCls} data-testid={`admin-event-speaker-name-${i}`} />
                        <input placeholder="Cargo / rol" value={sp.role || ''} onChange={(e) => setForm({ ...form, speakers: form.speakers.map((x, j) => j === i ? { ...x, role: e.target.value } : x) })} className={inputCls} />
                        <textarea rows={2} placeholder="Bio corta" value={sp.bio || ''} onChange={(e) => setForm({ ...form, speakers: form.speakers.map((x, j) => j === i ? { ...x, bio: e.target.value } : x) })} className={`${inputCls} sm:col-span-2`} />
                        <input placeholder="LinkedIn (opcional)" value={sp.linkedin || ''} onChange={(e) => setForm({ ...form, speakers: form.speakers.map((x, j) => j === i ? { ...x, linkedin: e.target.value } : x) })} className={`${inputCls} sm:col-span-2`} />
                        <div className="sm:col-span-2">
                          <ImageUploadOrUrl value={sp.photo} onChange={(url) => setForm({ ...form, speakers: form.speakers.map((x, j) => j === i ? { ...x, photo: url } : x) })} sizeHint="400 × 400 px (cuadrada)" testid={`admin-event-speaker-photo-${i}`} />
                        </div>
                      </div>
                      <button type="button" onClick={() => setForm({ ...form, speakers: form.speakers.filter((_, j) => j !== i) })} className="rounded-full bg-red-50 p-1.5 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={() => setForm({ ...form, speakers: [...(form.speakers || []), { ...EMPTY_SPEAKER }] })} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-cnib-blue/40 bg-cnib-blue/5 px-4 py-2 text-xs font-bold text-cnib-blue" data-testid="admin-event-speaker-add">
                  <UserPlus className="h-3.5 w-3.5" /> Agregar speaker
                </button>
              </div>

              {sectionTitle('5', 'Agenda y contenido')}
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Agenda (una línea por bloque / día)</label>
                  <textarea rows={6} value={form.agenda || ''} onChange={(e) => setForm({ ...form, agenda: e.target.value })} className={inputCls} placeholder="Día 1 - 09:00 Apertura&#10;Día 1 - 10:00 Panel macroeconómico&#10;Día 2 - 09:00 ..." />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Beneficios / Highlights (una línea por punto)</label>
                  <textarea rows={3} value={(form.highlights || []).join('\n')} onChange={(e) => setForm({ ...form, highlights: e.target.value.split('\n').filter(Boolean) })} className={inputCls} placeholder="Networking con 500 corredores&#10;Material descargable&#10;Certificado de participación" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Preguntas frecuentes (FAQ)</label>
                  <textarea rows={4} value={form.faq || ''} onChange={(e) => setForm({ ...form, faq: e.target.value })} className={inputCls} placeholder="P: ¿Dan factura? R: Sí, al correo del comprador…" />
                </div>
              </div>

              {sectionTitle('6', 'Entradas (tickets)')}
              <div className="space-y-2" data-testid="admin-event-tiers">
                {form.tiers.map((t, i) => (
                  <div key={i} className="grid grid-cols-[1fr_90px_90px_80px_30px] items-center gap-2">
                    <input value={t.name} onChange={(e) => setForm({ ...form, tiers: form.tiers.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} placeholder="Nombre" className={inputCls} />
                    <input type="number" min="0" value={t.price_usd} onChange={(e) => setForm({ ...form, tiers: form.tiers.map((x, j) => j === i ? { ...x, price_usd: e.target.value } : x) })} placeholder="Precio" className={inputCls} />
                    <input type="number" min="0" value={t.member_price_usd ?? ''} onChange={(e) => setForm({ ...form, tiers: form.tiers.map((x, j) => j === i ? { ...x, member_price_usd: e.target.value } : x) })} placeholder="Socio" className={inputCls} />
                    <input type="number" min="1" value={t.quantity} onChange={(e) => setForm({ ...form, tiers: form.tiers.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x) })} placeholder="Cupos" className={inputCls} />
                    <button type="button" onClick={() => setForm({ ...form, tiers: form.tiers.filter((_, j) => j !== i) })} className="text-red-500">×</button>
                  </div>
                ))}
                <button type="button" onClick={() => setForm({ ...form, tiers: [...form.tiers, { name: '', price_usd: 0, member_price_usd: null, is_free: false, quantity: 100 }] })} className="text-xs font-bold text-cnib-blue">+ Agregar tipo de entrada</button>
              </div>

              {sectionTitle('7', 'Auspiciadores y Partners')}
              <p className="-mt-1 mb-3 text-[11px] text-slate-500">Solo aparecerán en el micrositio si agregas al menos uno.</p>
              <div className="space-y-3" data-testid="admin-event-sponsors">
                {(form.sponsors || []).map((sp, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid={`admin-event-sponsor-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 grid gap-2 sm:grid-cols-2">
                        <input
                          placeholder="Nombre del auspiciador *"
                          required
                          value={sp.name}
                          onChange={(e) => setForm({ ...form, sponsors: form.sponsors.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })}
                          className={inputCls}
                          data-testid={`admin-event-sponsor-name-${i}`}
                        />
                        <select
                          value={sp.tier || 'Auspiciador'}
                          onChange={(e) => setForm({ ...form, sponsors: form.sponsors.map((x, j) => (j === i ? { ...x, tier: e.target.value } : x)) })}
                          className={inputCls}
                          data-testid={`admin-event-sponsor-tier-${i}`}
                        >
                          <option value="Auspiciador Platino">Auspiciador Platino</option>
                          <option value="Auspiciador Oro">Auspiciador Oro</option>
                          <option value="Auspiciador Plata">Auspiciador Plata</option>
                          <option value="Auspiciador">Auspiciador</option>
                          <option value="Partner">Partner</option>
                          <option value="Media Partner">Media Partner</option>
                          <option value="Patrocinador">Patrocinador</option>
                          <option value="Aliado">Aliado</option>
                        </select>
                        <input
                          placeholder="Sitio web / URL (opcional)"
                          value={sp.url || ''}
                          onChange={(e) => setForm({ ...form, sponsors: form.sponsors.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })}
                          className={`${inputCls} sm:col-span-2`}
                          data-testid={`admin-event-sponsor-url-${i}`}
                        />
                        <div className="sm:col-span-2">
                          <ImageUploadOrUrl
                            value={sp.logo}
                            onChange={(url) => setForm({ ...form, sponsors: form.sponsors.map((x, j) => (j === i ? { ...x, logo: url } : x)) })}
                            sizeHint="Logo PNG transparente (400 × 200 px)"
                            testid={`admin-event-sponsor-logo-${i}`}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, sponsors: form.sponsors.filter((_, j) => j !== i) })}
                        className="rounded-full bg-red-50 p-1.5 text-red-600"
                        aria-label="Eliminar auspiciador"
                        data-testid={`admin-event-sponsor-remove-${i}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, sponsors: [...(form.sponsors || []), { ...EMPTY_SPONSOR }] })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-cnib-blue/40 bg-cnib-blue/5 px-4 py-2 text-xs font-bold text-cnib-blue"
                  data-testid="admin-event-sponsor-add"
                >
                  <Plus className="h-3.5 w-3.5" /> Agregar auspiciador / partner
                </button>
              </div>

              {sectionTitle('8', 'Branding y CTAs')}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500 flex items-center gap-1"><Palette className="h-3 w-3" /> Color primario</label>
                  <input type="color" value={form.primary_color || '#F97316'} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-10 w-full rounded-xl border border-slate-200 bg-white" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Color de acento</label>
                  <input type="color" value={form.accent_color || '#0F4C81'} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-10 w-full rounded-xl border border-slate-200 bg-white" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Texto del CTA</label>
                  <input value={form.cta_text || ''} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Sitio web</label>
                  <input value={form.website || ''} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputCls} placeholder="https://..." />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Organizador</label>
                  <input value={form.organizer || ''} onChange={(e) => setForm({ ...form, organizer: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Stream en vivo (URL)</label>
                  <input value={form.stream_url || ''} onChange={(e) => setForm({ ...form, stream_url: e.target.value })} className={inputCls} placeholder="https://youtube.com/..." />
                </div>
              </div>

              <div className="sticky bottom-0 -mx-6 mt-6 flex items-center justify-end gap-2 border-t border-slate-100 bg-white px-6 py-4">
                <button type="button" onClick={() => { if (window.confirm('¿Cerrar sin guardar? Se perderán todos los cambios no guardados.')) setModalOpen(false); }} className="rounded-full px-5 py-2 text-sm font-bold text-slate-500">Cancelar</button>
                <button type="submit" disabled={saving} data-testid="admin-event-save" className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />} Guardar evento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {tickets && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4" onClick={() => setTickets(null)}>
          <div className="w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-5" style={{ maxHeight: '85vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-heading text-lg font-extrabold">Tickets vendidos ({tickets.list.length})</h3>
              <button onClick={() => setTickets(null)} className="rounded-full p-1"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-3 space-y-2">
              {tickets.list.map((t) => (
                <div key={t.ticket_id} className="flex items-center justify-between rounded-xl bg-slate-50 p-3 text-sm">
                  <div>
                    <p className="font-bold text-slate-900">{t.holder_name}</p>
                    <p className="text-xs text-slate-500">{t.holder_email} · {t.tier_name}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${t.checked_in ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                    {t.checked_in ? 'Check-in' : 'Pendiente'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
